
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Popover,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Link
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { OrderFormProps } from '../../lib/order';
import { APIHOST } from '../../common';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useApp } from '../../../src/components/AppContext';
import { fetchOrder } from '../../lib/order';


export default function HotelPaperwork ({
  orderId,
  back,
  onSave,
  type
}: OrderFormProps) {
  const [rewardsNumber, setRewardsNumber] = useState<string>('');
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const { setSnackbarInput } = useApp();

  useEffect(() => {
    // Fetch existing paperwork data for the order
    const fetchPaperwork = async () => {
      const data = await fetchOrder(orderId);
      setRewardsNumber(data?.rewardsNumber || '');
      setTermsAccepted(data?.termsAccepted || false);
    };

    fetchPaperwork();
  }, [orderId]);



  const handleRewardsNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setRewardsNumber(value);
  };

  const handleTermsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setTermsAccepted(checked);
  };

  const handleInfoClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setInfoAnchorEl(event.currentTarget);
  };

  const handleInfoClose = () => {
    setInfoAnchorEl(null);
  };

  const infoOpen = Boolean(infoAnchorEl);
  
  const savePaperwork = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        console.error('No authentication token found');
        return;
      }

      const response = await fetch(`${APIHOST}/api/orders/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          data: {
            termsAccepted,
            rewardsNumber: rewardsNumber || null,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save paperwork');
      }
      
      console.log('Paperwork saved successfully');
      setSnackbarInput({ message: 'Paperwork saved successfully', severity: 'success' });
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error('Error saving paperwork:', error);
      setSnackbarInput({ message: 'Error saving paperwork', severity: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight="600" sx={{ mb: 3 }}>
        Hotel Paperwork & Additional Information
      </Typography>

      {/* Rewards Number Field */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Rewards/Loyalty #"
          placeholder="Enter your rewards or loyalty program number"
          value={rewardsNumber}
          onChange={handleRewardsNumberChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="rewards program info"
                  onClick={handleInfoClick}
                  edge="end"
                  size="small"
                >
                  <InfoIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Popover
        open={infoOpen}
        anchorEl={infoAnchorEl}
        onClose={handleInfoClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="body2" fontWeight="600" gutterBottom>
            Rewards/Loyalty Program Information
          </Typography>
          <Typography variant="body2">
            If you have a rewards or loyalty program number with any of these hotels 
            (such as Marriott Bonvoy, Hilton Honors, IHG Rewards, etc.), enter it here 
            to ensure you receive points for your stay and any applicable member benefits.
          </Typography>
        </Box>
      </Popover>

      {/* Terms and Conditions Checkbox */}
      <FormControlLabel
        control={
          <Checkbox
            checked={termsAccepted}
            onChange={handleTermsChange}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            I agree to the{' '}
            <Link
              href="#"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                // TODO: Open terms and conditions modal or navigate to terms page
                console.log('Open terms and conditions');
              }}
            >
              hotel terms and conditions
            </Link>
          </Typography>
        }
        sx={{ mt: 2 }}
      />
      <Box>
        <Button
          color="primary"
          size="small"
          startIcon={<ArrowBackIcon fontSize="small" />}
          sx={{px: 4, py: 0.5, mt: 2}}
          onClick={back} 
          disabled={!back} >
          Back
        </Button>
        <Button
          color="primary"
          size="small"
          variant="contained"
          startIcon={<ArrowForwardIcon fontSize="small" />}
          sx={{px: 4, py: 0.5, mt: 2}}
          onClick={savePaperwork}
          disabled={!termsAccepted}
        >
          {type === 'step' ? 'Next' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
}